import { useState, useEffect, useCallback, useRef } from "react";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "./ui/select";
import { Input } from "./ui/input";
import { ColumnFilterProps, ColumnConfig } from "../types";

export function ColumnFilter<T>({
  column,
  filterType,
  filterOptions,
  theme,
  serverSide = false,
  debounceMs,
  onServerFilterChange,
}: ColumnFilterProps<T>) {
  const [localValue, setLocalValue] = useState<string>("");

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isDebouncing, setIsDebouncing] = useState(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>(null);
  const columnFilterValue = serverSide ? localValue : column.getFilterValue();

  useEffect(() => {
    if (!serverSide) {
      setLocalValue((columnFilterValue as string) || "");
    }
  }, [columnFilterValue, serverSide]);

  const debouncedServerFilterChange = useCallback(
    (columnId: string, value: string) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      setIsDebouncing(true);

      debounceTimerRef.current = setTimeout(() => {
        if (onServerFilterChange) {
          onServerFilterChange(columnId, value);
        }

        setIsDebouncing(false);
      }, debounceMs);
    },
    [onServerFilterChange, debounceMs]
  );

  const handleValueChange = (value: string) => {
    setLocalValue(value);

    if (serverSide && onServerFilterChange) {
      const columnId =
        (column.columnDef.meta as { columnConfig: ColumnConfig<T> })
          .columnConfig.serverFilterKey || column.id;
      if (
        filterType === "text" ||
        filterType === "number" ||
        filterType === "date"
      ) {
        debouncedServerFilterChange(columnId, value || "");
      } else {
        onServerFilterChange(columnId, value === "all" ? "" : value);
      }
    } else {
      if (
        filterType === "text" ||
        filterType === "date" ||
        filterType === "number"
      ) {
        column.setFilterValue(value || undefined);
      } else column.setFilterValue(value === "all" ? "" : value);
    }
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  if (filterType === "select" && filterOptions) {
    return (
      <Select value={localValue || "all"} onValueChange={handleValueChange}>
        <SelectTrigger
          className={`h-8 rounded-full ${theme.rowEvenBg} ${theme.rowText} ${theme.buttonSecondaryBorder}`}
        >
          <SelectValue placeholder="All" />
        </SelectTrigger>
        <SelectContent className={`rounded-[1rem] ${theme.rowEvenBg}`}>
          <SelectItem
            value="all"
            className={`rounded-[1rem] ${theme.rowText} ${theme.buttonSecondaryHover}`}
          >
            All
          </SelectItem>
          {filterOptions.map((option) => (
            <SelectItem
              key={option.value}
              value={String(option.value)}
              className={`rounded-[1rem] ${theme.rowText} ${theme.buttonSecondaryHover}`}
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <Input
      type={
        filterType === "date"
          ? "date"
          : filterType === "number"
          ? "number"
          : "text"
      }
      placeholder={filterType === "date" ? "Select date..." : "Filter..."}
      value={localValue}
      onChange={(e) => {
        handleValueChange(e.target.value);
      }}
      className={`h-8 rounded-full ${theme.rowText} ${theme.rowEvenBg} ${theme.buttonSecondaryBorder}`}
    />
  );
}
